/**
 * Sidebar — primary navigation. Mirrors the Opus Django app modules.
 *
 * The nav structure is built inside the component so badge counts can be
 * driven by the live cross-vendor inventory store. Adding a new page is
 * still one entry; the only difference from a static list is that
 * Devices/Meraki/Aruba pull their badge from `useInventory()`.
 *
 * Inventory is loaded once when the sidebar mounts. The store dedupes
 * concurrent loads, so other pages calling load() at the same time
 * don't double-fetch.
 */
import { useEffect, useMemo, Fragment, useState, useRef } from "react";
import { NavLink } from "react-router-dom";
import {
  X,
  LayoutGrid,
  Server,
  GitBranch,
  Building2,
  Network,
  Layers,
  ScrollText,
  Sparkles,
  Workflow,
  TableProperties,
  LineChart,
  Shield,
  ClipboardCheck,
  Users,
  Stethoscope,
  FileCheck2,
  Radar,
  Search,
  PanelLeftClose,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { Input } from "@/components/ui/input";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";
import { useAuth } from "@/stores/auth";
import { useInventory } from "@/stores/inventory";

interface NavLeaf {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: string;
}
interface NavGroup {
  /** Optional group label rendered above its items. */
  label?: string;
  items: NavLeaf[];
}

/** Format a device count for the sidebar pill — e.g. 2320 → "2.3k",
 *  54784 → "55k", 1234567 → "1.2M". Returns "—" when undefined so the
 *  pill width stays stable while the inventory loads. */
function formatCount(n: number | undefined): string {
  if (n === undefined) return "—";
  if (n < 1000) return String(n);
  if (n < 1_000_000) {
    const k = n / 1000;
    return k >= 10 ? `${Math.round(k)}k` : `${k.toFixed(1)}k`;
  }
  const m = n / 1_000_000;
  return m >= 10 ? `${Math.round(m)}M` : `${m.toFixed(1)}M`;
}

interface SidebarProps {
  showCloseButton?: boolean;
  onClose?: () => void;
  onNavigate?: () => void;
}

export function Sidebar({
  showCloseButton,
  onClose,
  onNavigate,
}: SidebarProps) {
  const user = useAuth((s) => s.user);
  const arubaLandingTo =
    user?.arubaKeyConfigured === true ? "/aruba/devices" : "/aruba/keys";
  const [searchQuery, setSearchQuery] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [fadeOpacity, setFadeOpacity] = useState(0);
  const animatedNavRef = useUiAutoAnimate();

  // Cross-vendor inventory drives the device-count pills. Sidebar mounts
  // once per session (it's part of the AppShell) so calling load() here
  // doesn't multiply the per-page cost — the store also no-ops concurrent
  // load() calls.
  const inventoryStatus = useInventory((s) => s.status);
  const inventoryData = useInventory((s) => s.data);
  const loadInventory = useInventory((s) => s.load);
  useEffect(() => {
    if (inventoryStatus === "idle") void loadInventory();
  }, [inventoryStatus, loadInventory]);

  // Per-vendor counters for the badges. Computed once per inventory change.
  const counts = useMemo(() => {
    const total = inventoryData.length;
    const meraki = inventoryData.filter((d) => d.vendor === "meraki").length;
    const aruba = inventoryData.filter((d) => d.vendor === "aruba").length;
    return { total, meraki, aruba };
  }, [inventoryData]);

  // Calculate fade indicator opacity based on scroll position.
  // Opacity linearly decreases as user scrolls closer to bottom.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isScrollable = scrollHeight > clientHeight;
      let newOpacity = 0;
      if (isScrollable) {
        const distanceFromBottom = scrollHeight - clientHeight - scrollTop;
        const fadeZone = 80;
        newOpacity =
          distanceFromBottom <= 0 ? 0
          : distanceFromBottom >= fadeZone ? 1
          : distanceFromBottom / fadeZone;
      }
      setFadeOpacity(newOpacity);
    };

    checkScroll();
    container.addEventListener("scroll", checkScroll, { passive: true });
    // Re-check when content changes (e.g., search filters items)
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", checkScroll);
      resizeObserver.disconnect();
    };
  }, [searchQuery]);

  // Build the nav and filter in one memo so the NAV array reference is stable.
  const ready = inventoryStatus === "ready" || inventoryStatus === "error";
  const filteredNav = useMemo(() => {
    const nav: NavGroup[] = [
      {
        items: [
          { to: "/", label: "Overview", icon: LayoutGrid },
          { to: "/sites", label: "Sites", icon: Building2 },
          {
            to: "/devices",
            label: "Devices",
            icon: Server,
            badge: formatCount(ready ? counts.total : undefined),
          },
          { to: "/topology", label: "Topology", icon: GitBranch },
        ],
      },
      {
        label: "Vendors",
        items: [
          {
            to: "/meraki/provisioning",
            label: "Meraki",
            icon: Network,
            badge: formatCount(ready ? counts.meraki : undefined),
          },
          {
            to: arubaLandingTo,
            label: "Aruba",
            icon: Layers,
            badge: formatCount(ready ? counts.aruba : undefined),
          },
        ],
      },
      {
        label: "Configuration",
        items: [
          { to: "/configuration-generator", label: "Firewall", icon: ScrollText },
          { to: "/switch", label: "Switch", icon: Layers },
        ],
      },
      {
        label: "Automation",
        items: [
          { to: "/workflows", label: "Workflows", icon: Workflow },
          { to: "/compliance", label: "Compliance", icon: ClipboardCheck },
          { to: "/workbook", label: "Workbook", icon: TableProperties },
          { to: "/monitor", label: "Monitor", icon: LineChart },
        ],
      },
      {
        label: "AI",
        items: [
          { to: "/diagnose", label: "Diagnose", icon: Stethoscope },
          { to: "/closeout", label: "Closeout", icon: FileCheck2 },
          { to: "/watch", label: "Site Watch", icon: Radar },
          // Dedicated history page — full-page view of the persisted transcript
          // (per-browser). Richer than the audit-log filter because it renders
          // markdown answers + proposal cards inline.
          { to: "/agent", label: "Agent history", icon: Sparkles },
        ],
      },
      {
        label: "Admin",
        items: [
          { to: "/audit", label: "Audit Log", icon: Shield },
          { to: "/users", label: "Users & Tenants", icon: Users },
        ],
      },
      {
        label: "Developer",
        items: [{ to: "/design-system", label: "Design System", icon: Sparkles }],
      },
    ];
    if (!searchQuery.trim()) return nav;
    const q = searchQuery.toLowerCase();
    return nav.flatMap((group) => {
      const items = group.items.filter((item) =>
        item.label.toLowerCase().includes(q),
      );
      return items.length > 0 ? [{ ...group, items }] : [];
    });
  }, [searchQuery, ready, counts, arubaLandingTo]);

  return (
    <aside className="flex h-full w-[252px] shrink-0 flex-col gap-1 overflow-hidden border-r border-line bg-surface/30 pl-3 py-4 backdrop-blur-sm">
      {showCloseButton ? (
        <div className="mb-1 flex items-center justify-between px-1 pb-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-mute">
            Navigation
          </span>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-surface text-fg-dim hover:bg-surface-2 transition-colors"
            aria-label="Close sidebar"
          >
            <X size={15} />
          </button>
        </div>
      ) : null}

      {/* Scroll container wrapper with fixed fade indicator */}
      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollContainerRef}
          className="h-full overflow-y-auto [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-line/30 hover:[&::-webkit-scrollbar-thumb]:bg-line/50 [&::-webkit-scrollbar-track]:bg-transparent"
        >
          <div ref={animatedNavRef} className="pr-2">
            {filteredNav.length > 0 ? (
              filteredNav.map((group, gi) => (
                <Fragment key={gi}>
                  {group.label ? (
                    <div className="px-3 pb-1.5 pt-4 font-mono text-[10px] uppercase tracking-[0.14em] text-fg-mute">
                      {group.label}
                    </div>
                  ) : null}
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          "relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13.5px] font-medium transition-colors",
                          "text-fg-dim hover:bg-surface-2/60 hover:text-fg",
                          isActive &&
                            "bg-aurora-soft text-violet shadow-[inset_0_0_0_1px_rgba(125,108,255,0.18)] hover:text-violet",
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {/* Left accent bar — only when active. */}
                          {isActive ? (
                            <span
                              aria-hidden="true"
                              className="absolute -left-3 top-2.5 bottom-2.5 w-[3px] rounded-r bg-aurora-grad"
                            />
                          ) : null}
                          <item.icon
                            size={16}
                            className={
                              isActive ? "text-violet" : "text-fg-mute"
                            }
                          />
                          <span className="flex-1">{item.label}</span>
                          {item.badge ? (
                            <span className="rounded-full bg-line/50 px-1.5 py-0.5 font-mono text-[10px] text-fg-dim">
                              {item.badge}
                            </span>
                          ) : null}
                        </>
                      )}
                    </NavLink>
                  ))}
                </Fragment>
              ))
            ) : (
              <div className="px-3 py-8 text-center text-xs text-fg-mute">
                {searchQuery ? "No matches found" : "No items"}
              </div>
            )}
          </div>
        </div>

        {/* Fade indicator fixed at bottom of visible area */}
        {fadeOpacity > 0 ? (
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-surface via-surface/90 to-transparent transition-opacity duration-150"
            style={{ opacity: fadeOpacity }}
            aria-hidden="true"
          />
        ) : null}
      </div>

      {/* Search bar and close button */}
      <div className="mt-auto border-t border-line pt-3 pr-2">
        <div className="mb-2 flex items-center gap-2">
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-9 pr-3 text-xs"
            />
          </div>
          {onClose && !showCloseButton ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line bg-surface text-fg-dim hover:bg-surface-2 hover:text-fg transition-colors"
              aria-label="Close sidebar"
              title="Close sidebar"
            >
              <PanelLeftClose size={16} />
            </button>
          ) : null}
        </div>
      </div>
    </aside>
  );
}
